import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, Plane, Clock, PackageCheck, Clock3,
  Zap, Send, Megaphone, Volume2, Globe, CheckCircle2, AlertTriangle, Check, X, Pencil, Timer,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { beltAssignmentsAPI } from '../../services/api';
import { useSupervisorSocket } from '../../hooks/useGateSocket';
import EmptyState from '../../components/UI/EmptyState';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import { isEnglishOnly } from '../../utils/languageLabel';

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function toTimeInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function timeInputToDate(timeStr, referenceIso) {
  const ref = referenceIso ? new Date(referenceIso) : new Date();
  const [h, m] = timeStr.split(':').map(Number);
  const d = new Date(ref);
  d.setHours(h, m, 0, 0);
  return d;
}

function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 0) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
}

// Same tones as the other three pages showing this same AirportAnnouncement
// status set (Operator/Supervisor Approvals, Operator Announcements).
const STATUS_CFG = {
  pending:      { label: 'Pending',      tone: 'neutral' },
  generating:   { label: 'Generating',   tone: 'info' },
  ready:        { label: 'Ready',        tone: 'success' },
  broadcasting: { label: 'Broadcasting', tone: 'info', pulse: true },
  played:       { label: 'Played',       tone: 'neutral' },
  rejected:     { label: 'Rejected',     tone: 'danger' },
  cancelled:    { label: 'Cancelled',    tone: 'neutral' },
};

// Timeline entry type, derived from the announcement title convention set by
// flightSyncService.js / weatherAlertService.js / the ad-hoc endpoint below.
function entryType(title) {
  if (title.startsWith('Belt announcement')) return { label: 'Belt Change', badge: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-400' };
  if (title.startsWith('First bag')) return { label: 'First Bag', badge: 'bg-teal-100 text-teal-700', dot: 'bg-teal-400' };
  if (title.startsWith('Disembarkation delay')) return { label: 'Disembarkation Delay', badge: 'bg-red-100 text-red-700', dot: 'bg-red-400' };
  if (title.startsWith('Disembarkation resumed')) return { label: 'Disembarkation Resumed', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' };
  if (title.startsWith('Ad-hoc')) return { label: 'Ad-hoc', badge: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' };
  return { label: title, badge: 'bg-slate-100 text-slate-600', dot: 'bg-slate-300' };
}

function deriveStatus(a) {
  if (a.disembark_resumed_announced) return { label: 'Disembarkation Resumed', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' };
  if (a.disembark_delay_announced) return { label: 'Disembarkation Delayed', cls: 'bg-red-50 text-red-700 border border-red-200' };
  if (!a.touchdown_at) return { label: 'Awaiting Arrival', cls: 'bg-gray-100 text-gray-500 border border-gray-200' };
  if (!a.first_bag_at) return { label: 'Landed · Bags Pending', cls: 'bg-blue-50 text-blue-700 border border-blue-200' };
  return { label: 'Bags Delivered', cls: 'bg-emerald-50 text-emerald-600 border border-emerald-100' };
}

// Inline click-to-edit for scheduled_arrival_time — same interaction as Gate Queue's
// TimeEditor (click the clock → HH:MM input → save/cancel), applied to the one field on
// this page that can legitimately be wrong and worth correcting: the flight's scheduled
// arrival time as reported by FlightMS. Unlike Gate Queue's per-item editor, this isn't
// rescheduling a pending announcement — nothing here is pending, everything already
// played — it's correcting the flight record itself.
function ScheduledTimeEditor({ assignmentId, scheduledArrivalTime, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(toTimeInput(scheduledArrivalTime));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setValue(toTimeInput(scheduledArrivalTime)); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open, scheduledArrivalTime]);

  async function save() {
    const newDate = timeInputToDate(value, scheduledArrivalTime);
    if (isNaN(newDate.getTime())) { toast.error('Invalid time'); return; }
    setSaving(true);
    try {
      await beltAssignmentsAPI.updateScheduledTime(assignmentId, newDate.toISOString());
      toast.success('Scheduled time updated');
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update time');
    } finally {
      setSaving(false);
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') setOpen(false);
  }

  if (open) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="time"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKey}
          className="border border-blue-400 rounded-lg px-2 py-0.5 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-400 w-[8rem] min-w-[8rem] bg-white text-slate-900"
        />
        <button onClick={save} disabled={saving} className="p-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
        </button>
        <button onClick={() => setOpen(false)} className="p-1 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/10 transition-colors">
          <X size={10} />
        </button>
      </span>
    );
  }

  return (
    <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 group/timebtn hover:text-white transition-colors" title="Click to correct the scheduled time">
      <span>Sched {fmtTime(scheduledArrivalTime)}</span>
      <Pencil size={9} className="opacity-0 group-hover/timebtn:opacity-100 transition-opacity" />
    </button>
  );
}

// Quick +/- minute shift for this flight's ENTIRE timeline — mirrors Gate Queue's
// "Shift all" bar, but since nothing on this page is pending (everything already played),
// shifting has to carry the scheduled arrival time, touchdown/first-bag milestones, and
// every timeline announcement's timestamp along together via PATCH /:id/shift, or the
// header time drifts out of sync with the milestones and timeline shown below it.
function ShiftBar({ assignmentId, onShifted }) {
  const [shifting, setShifting] = useState(false);

  async function shift(delta) {
    setShifting(true);
    try {
      await beltAssignmentsAPI.shiftTime(assignmentId, delta);
      toast.success(`Timeline shifted ${delta > 0 ? '+' : ''}${delta}m`);
      onShifted();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to shift timeline');
    } finally {
      setShifting(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-2xl mb-4">
      <div className="flex items-center gap-1.5 flex-shrink-0 w-full sm:w-auto">
        {shifting ? <Loader2 size={13} className="animate-spin text-amber-500" /> : <Timer size={13} className="text-amber-600" />}
        <span className="text-xs font-bold text-amber-700">Shift timeline</span>
      </div>

      <div className="hidden sm:block h-4 w-px bg-amber-200" />

      {/* Earlier */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide mr-0.5">Earlier</span>
        {[-15, -10, -5].map(d => (
          <button key={d} onClick={() => shift(d)} disabled={shifting}
            className="px-2.5 py-1 rounded-xl text-xs font-bold border border-blue-200 text-blue-700 bg-white hover:bg-blue-50 transition-colors disabled:opacity-50 shadow-sm">
            {d}m
          </button>
        ))}
      </div>

      <div className="hidden sm:block h-4 w-px bg-amber-200" />

      {/* Later */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide mr-0.5">Later</span>
        {[5, 10, 15].map(d => (
          <button key={d} onClick={() => shift(d)} disabled={shifting}
            className="px-2.5 py-1 rounded-xl text-xs font-bold border border-orange-200 text-orange-700 bg-white hover:bg-orange-50 transition-colors disabled:opacity-50 shadow-sm">
            +{d}m
          </button>
        ))}
      </div>
    </div>
  );
}

function TimelineAudio({ path }) {
  const [show, setShow] = useState(false);
  if (!path) return null;
  const url = `/uploads/${path.split('/').pop()}`;
  return show ? (
    <audio controls autoPlay src={url} className="h-8 mt-1.5" />
  ) : (
    <button
      onClick={() => setShow(true)}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline mt-1.5"
    >
      <Volume2 size={12} /> Preview audio
    </button>
  );
}

export default function BeltAssignmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.pathname.startsWith('/admin') ? '/admin/belt-assignments' : '/supervisor/belts';

  const [assignment, setAssignment] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [adhocText, setAdhocText] = useState('');
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const [aRes, tRes] = await Promise.all([
        beltAssignmentsAPI.getById(id),
        beltAssignmentsAPI.getTimeline(id),
      ]);
      setAssignment(aRes.data.data);
      setTimeline(tRes.data.data || []);
    } catch {
      toast.error('Failed to load belt assignment');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);

  useSupervisorSocket({
    onEvent: (evt) => {
      if (['announcement:status_change', 'announcement:submitted'].includes(evt)) load(true);
    },
  });

  async function handleSendAdhoc(e) {
    e.preventDefault();
    if (!adhocText.trim()) return;
    setSending(true);
    try {
      await beltAssignmentsAPI.sendAdhoc(id, adhocText.trim());
      toast.success('Ad-hoc announcement broadcast');
      setAdhocText('');
      setAdhocOpen(false);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to broadcast');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-400">Loading belt assignment…</span>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500 font-semibold">Belt assignment not found</p>
      </div>
    );
  }

  const status = deriveStatus(assignment);
  const nowPlaying = timeline.find(t => t.status === 'broadcasting');
  const upcoming = timeline.filter(t => ['pending', 'generating', 'ready'].includes(t.status));
  const history = timeline.filter(t => ['played', 'cancelled', 'rejected'].includes(t.status))
    .sort((a, b) => new Date(b.played_at || b.createdAt) - new Date(a.played_at || a.createdAt));

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      {/* ── Now Playing banner ── */}
      {nowPlaying && (
        <div className="mb-4 flex items-center gap-3 bg-blue-600 text-white rounded-xl px-4 py-3 shadow-lg animate-pulse">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Megaphone size={16} />
            <span className="text-xs font-bold uppercase tracking-wide">Now Playing</span>
          </div>
          <p className="text-sm font-medium truncate flex-1">{nowPlaying.english_text}</p>
          <Volume2 size={16} className="flex-shrink-0 opacity-80" />
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(backPath)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors flex-shrink-0">
          <ArrowLeft size={18} />
        </button>

        <div className="flex-1 min-w-0 bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl px-4 py-3 shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h1 className="text-2xl font-black text-white tracking-tight">{assignment.flight_number}</h1>
                {assignment.terminal && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-lg bg-white/20 text-white/90 backdrop-blur">
                    {assignment.terminal}
                  </span>
                )}
                <span className="text-xs font-bold px-2 py-0.5 rounded-lg bg-white/10 text-white/70">
                  Belt {assignment.belt_number || '—'}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 text-white/80 text-sm">
                  <Plane size={13} className="text-white/60" />
                  <span className="font-semibold text-white">{assignment.airline}</span>
                  {assignment.origin && <><span className="text-white/60">from</span><span>{assignment.origin}</span></>}
                </div>
                <span className="text-white/40 text-xs">·</span>
                <span className="text-white/70 text-sm">
                  <ScheduledTimeEditor
                    assignmentId={assignment.id}
                    scheduledArrivalTime={assignment.scheduled_arrival_time}
                    onSaved={() => load(true)}
                  />
                </span>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span className={`text-xs font-black px-2.5 py-1 rounded-xl ${status.cls.includes('red') ? 'bg-red-500/30 text-red-300' : 'bg-blue-500/30 text-blue-300'}`}>
                {status.label}
              </span>
              {assignment.belt_change_count > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-lg font-semibold bg-amber-500/20 text-amber-300">
                  changed ×{assignment.belt_change_count}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg font-semibold bg-white/10 text-white/60">
                <Globe size={9} />
                {isEnglishOnly(assignment.secondary_language) ? 'English only' : assignment.secondary_language}
                {assignment.language_auto_detected && <span className="text-emerald-400">auto</span>}
              </span>
            </div>
          </div>
        </div>

        <button onClick={() => load(true)} disabled={refreshing} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 flex-shrink-0 transition-colors" title="Refresh">
          <RefreshCw size={16} className={refreshing ? 'animate-spin text-blue-500' : ''} />
        </button>
      </div>

      {/* ── Shift-all bar ── */}
      <ShiftBar
        assignmentId={assignment.id}
        onShifted={() => load(true)}
      />

      {/* ── Milestones ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        <Milestone icon={PackageCheck} label="Touchdown" done={!!assignment.touchdown_at} value={assignment.touchdown_at ? timeAgo(assignment.touchdown_at) : 'not yet'} />
        <Milestone icon={Zap} label="Belt Assigned" done={!!assignment.last_announcement_id} value={assignment.belt_number ? `Belt ${assignment.belt_number}` : '—'} />
        <Milestone icon={Volume2} label="First Bag" done={!!assignment.first_bag_at} value={assignment.first_bag_at ? timeAgo(assignment.first_bag_at) : 'not yet'} />
        <Milestone
          icon={assignment.disembark_resumed_announced ? CheckCircle2 : AlertTriangle}
          label="Disembarkation"
          done={assignment.disembark_resumed_announced}
          warn={assignment.disembark_delay_announced && !assignment.disembark_resumed_announced}
          value={assignment.disembark_resumed_announced ? 'Resumed' : assignment.disembark_delay_announced ? 'Delayed' : 'Normal'}
        />
      </div>

      {/* ── Ad-hoc ── */}
      <div className="mb-5">
        {!adhocOpen ? (
          <button
            onClick={() => setAdhocOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition-colors shadow-sm"
          >
            <Zap size={14} /> Send Ad-hoc to {assignment.terminal || 'this'} zone
          </button>
        ) : (
          <form onSubmit={handleSendAdhoc} className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
            <p className="text-xs font-bold text-purple-700 mb-3 flex items-center gap-1.5 uppercase tracking-wide">
              <Zap size={12} /> Ad-hoc announcement — {assignment.terminal} zone
            </p>
            <textarea
              value={adhocText}
              onChange={e => setAdhocText(e.target.value)}
              placeholder={`Type the announcement text for ${assignment.flight_number}…`}
              rows={3}
              maxLength={1000}
              autoFocus
              className="w-full border border-purple-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none bg-white shadow-sm"
            />
            <div className="flex items-center justify-between gap-2 mt-3">
              <span className="text-xs text-slate-400">{adhocText.length}/1000 · Translation & audio auto-generated</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={sending} onClick={() => setAdhocOpen(false)} type="button">
                  Cancel
                </Button>
                <button type="submit" disabled={sending || !adhocText.trim()}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50">
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  {sending ? 'Broadcasting…' : 'Broadcast now'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* ── Upcoming ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-black text-slate-700 uppercase tracking-wide">Upcoming</h2>
          <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
            {upcoming.length}
          </span>
        </div>

        {timeline.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="No announcements yet"
            subtitle="This flight hasn't triggered any belt/baggage announcements."
          />
        ) : upcoming.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-slate-200">
            <Megaphone size={28} className="text-slate-200 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Nothing queued for this flight right now.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {upcoming.map(item => {
              const tc = entryType(item.title);
              const sc = STATUS_CFG[item.status] || STATUS_CFG.pending;
              return (
                <div key={item.id} className="flex items-stretch bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className={`w-1 flex-shrink-0 ${tc.dot}`} />
                  <div className="flex-1 min-w-0 px-3 py-3">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-lg ${tc.badge}`}>{tc.label}</span>
                      <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} />
                      <span className="text-xs text-slate-400 ml-auto flex items-center gap-1">
                        <Clock size={10} /> {fmtTime(item.played_at || item.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 font-medium leading-snug">{item.english_text}</p>
                    {item.secondary_text && (
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                        <Globe size={10} className="flex-shrink-0 text-slate-300" />
                        <span className="italic">{item.secondary_text}</span>
                      </p>
                    )}
                    <TimelineAudio path={item.audio_file_path} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Played History — collapsible, mirrors Gate Queue's Played History section ── */}
      {history.length > 0 && (
        <div className="mt-5">
          <button onClick={() => setShowHistory(h => !h)}
            className="flex items-center gap-2 w-full px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors">
            <div className="flex-1 flex items-center gap-2">
              <span className="text-xs font-black text-slate-500 uppercase tracking-wide">Played History</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">
                {history.length}
              </span>
            </div>
            {showHistory ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
          </button>

          {showHistory && (
            <div className="mt-2 flex flex-col gap-1.5 pl-2 border-l-2 border-slate-100 ml-3">
              {history.map(item => {
                const tc = entryType(item.title);
                const sc = STATUS_CFG[item.status] || STATUS_CFG.played;
                return (
                  <div key={item.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-3 py-2.5 opacity-60">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${tc.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-slate-600">{tc.label}</span>
                        <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} size="sm" />
                        <span className="text-xs text-slate-400 ml-auto flex items-center gap-1">
                          <Clock size={10} /> {fmtTime(item.played_at || item.createdAt)}
                        </span>
                      </div>
                      {item.english_text && (
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.english_text}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Milestone({ icon: Icon, label, done, warn, value }) {
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${
      warn ? 'bg-red-50 border-red-200' : done ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'
    }`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon size={12} className={warn ? 'text-red-500' : done ? 'text-emerald-500' : 'text-slate-300'} />
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</span>
      </div>
      <p className={`text-sm font-bold ${warn ? 'text-red-700' : done ? 'text-emerald-700' : 'text-slate-400'}`}>{value}</p>
    </div>
  );
}
